// frontend/src/pages/TermosDeUso.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function TermosDeUso() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/termos-de-uso.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'termos-de-uso.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            .print-content .bg-gray-50 { background: none; padding: 0; border: none; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Termos de Uso</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">TERMOS DE USO</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                Bem-vindo ao Brechó Vizinho. Estes Termos de Uso estabelecem as regras para utilização da plataforma Brechó Vizinho. 
                Ao criar uma conta, acessar ou utilizar a plataforma, o usuário declara que leu, compreendeu e concorda integralmente 
                com estes Termos.
              </p>

              {/* 1. SOBRE O BRECHÓ VIZINHO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. SOBRE O BRECHÓ VIZINHO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho é uma plataforma digital comunitária destinada à divulgação de anúncios, doações, oportunidades e 
                  informações de interesse entre moradores de condomínios, loteamentos e comunidades participantes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A plataforma tem como objetivo incentivar o reaproveitamento de bens, a economia colaborativa, a interação entre 
                  vizinhos e o fortalecimento das comunidades locais.
                </p>
              </div>

              {/* 2. NATUREZA DA PLATAFORMA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. NATUREZA DA PLATAFORMA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho atua exclusivamente como meio de divulgação de anúncios e comunicação entre usuários.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  <strong>O Brechó Vizinho NÃO:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Compra produtos;</li>
                  <li>Vende produtos;</li>
                  <li>Intermedeia negociações;</li>
                  <li>Recebe pagamentos;</li>
                  <li>Processa pagamentos;</li>
                  <li>Realiza entregas;</li>
                  <li>Armazena mercadorias;</li>
                  <li>Oferece garantias sobre produtos ou serviços;</li>
                  <li>Representa compradores ou vendedores.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Todas as negociações são realizadas diretamente entre os usuários.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os usuários são os únicos responsáveis pelos produtos anunciados, pelas informações publicadas, pelos acordos 
                  firmados, pelos pagamentos efetuados e pelas entregas realizadas.
                </p>
              </div>

              {/* 3. CADASTRO DE USUÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. CADASTRO DE USUÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Para utilizar determinadas funcionalidades da plataforma, o usuário deverá realizar cadastro.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário declara que fornecerá informações verdadeiras, completas e atualizadas.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá solicitar informações complementares para validação do cadastro.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O fornecimento de informações falsas poderá resultar na suspensão ou exclusão da conta.
                </p>
              </div>

              {/* 4. REQUISITO DE IDADE - NUEVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. REQUISITO DE IDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização da plataforma é permitida apenas para pessoas com 18 (dezoito) anos ou mais.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Ao criar uma conta, o usuário declara possuir capacidade legal para aceitar estes Termos de Uso e utilizar os 
                  serviços disponibilizados pelo Brechó Vizinho.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Caso seja identificado cadastro realizado por menor de idade, a plataforma poderá suspender ou excluir a conta a 
                  qualquer momento.
                </p>
              </div>

              {/* 5. VALIDAÇÃO DE CADASTRO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. VALIDAÇÃO DE CADASTRO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Com o objetivo de preservar a segurança da comunidade, o cadastro poderá ser submetido à validação.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A validação poderá ser realizada por:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Proprietário previamente validado do imóvel;</li>
                  <li>Administrador da plataforma;</li>
                  <li>Síndico;</li>
                  <li>Administração do condomínio;</li>
                  <li>Associação de moradores;</li>
                  <li>Conselho gestor da comunidade.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O usuário autoriza o compartilhamento das informações estritamente necessárias para a validação do vínculo 
                  residencial e comunitário.
                </p>
              </div>

              {/* 6. PERFIS DE USUÁRIO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. PERFIS DE USUÁRIO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá disponibilizar diferentes categorias de usuários, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Proprietário;</li>
                  <li>Morador.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá criar novas categorias de usuários no futuro sem necessidade de alteração destes Termos.
                </p>
              </div>

              {/* 7. DIREITOS SOBRE IMAGENS - NUEVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. DIREITOS SOBRE IMAGENS</h2>
                <p className="text-gray-600 leading-relaxed">
                  O usuário declara possuir os direitos necessários para utilizar e publicar as imagens enviadas à plataforma.
                </p>
              </div>

              {/* 8. PUBLICAÇÃO DE ANÚNCIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. PUBLICAÇÃO DE ANÚNCIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Ao publicar um anúncio, o usuário declara que:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>É proprietário legítimo do item anunciado ou possui autorização para sua venda ou doação;</li>
                  <li>As informações fornecidas são verdadeiras;</li>
                  <li>As imagens utilizadas correspondem ao item anunciado;</li>
                  <li>O anúncio não viola legislação vigente nem direitos de terceiros.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O usuário é integralmente responsável pelo conteúdo publicado.
                </p>
              </div>

              {/* 9. CONTEÚDOS E PRODUTOS PROIBIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. CONTEÚDOS E PRODUTOS PROIBIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  É proibida a publicação de conteúdos ou produtos cuja comercialização, divulgação ou circulação seja vedada por lei.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Incluem-se, entre outros:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Armas e munições;</li>
                  <li>Drogas ilícitas;</li>
                  <li>Medicamentos controlados sem autorização legal;</li>
                  <li>Produtos falsificados;</li>
                  <li>Material pornográfico;</li>
                  <li>Animais silvestres;</li>
                  <li>Produtos obtidos por meios ilícitos;</li>
                  <li>Conteúdo discriminatório, ofensivo ou que incentive violência.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá remover qualquer conteúdo que considere inadequado ou incompatível com os valores da 
                  comunidade.
                </p>
              </div>

              {/* 10. RESPONSABILIDADE DOS USUÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. RESPONSABILIDADE DOS USUÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Cada usuário é integralmente responsável por:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Suas publicações;</li>
                  <li>Seus anúncios;</li>
                  <li>Suas negociações;</li>
                  <li>Seus pagamentos;</li>
                  <li>Suas entregas;</li>
                  <li>Suas comunicações com terceiros.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-2">
                  <strong>O Brechó Vizinho não se responsabiliza por:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Fraudes;</li>
                  <li>Golpes;</li>
                  <li>Inadimplência;</li>
                  <li>Defeitos de produtos;</li>
                  <li>Divergências entre usuários;</li>
                  <li>Danos diretos ou indiretos decorrentes de negociações realizadas através da plataforma.</li>
                </ul>
              </div>

              {/* 11. DENÚNCIAS E MODERAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. DENÚNCIAS E MODERAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Qualquer usuário poderá denunciar anúncios ou conteúdos considerados inadequados.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Remover conteúdos;</li>
                  <li>Suspender anúncios;</li>
                  <li>Restringir funcionalidades;</li>
                  <li>Suspender contas;</li>
                  <li>Excluir usuários.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As medidas poderão ser adotadas sem aviso prévio quando necessário para proteção da comunidade.
                </p>
              </div>

              {/* 12. PLANOS GRATUITOS E RECURSOS PAGOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. PLANOS GRATUITOS E RECURSOS PAGOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá disponibilizar recursos gratuitos e pagos.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os limites de anúncios gratuitos poderão ser alterados a qualquer momento.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá oferecer:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Pacotes de anúncios;</li>
                  <li>Destaques de anúncios;</li>
                  <li>Espaços publicitários;</li>
                  <li>Serviços promocionais.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A contratação de recursos pagos não garante vendas, contatos, visualizações mínimas ou qualquer resultado comercial 
                  específico.
                </p>
              </div>

              {/* 13. PUBLICIDADE E PATROCÍNIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. PUBLICIDADE E PATROCÍNIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá exibir conteúdos patrocinados, anúncios publicitários e materiais promocionais.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A exibição de publicidade não representa recomendação, garantia, certificação ou endosso por parte do Brechó Vizinho.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os anunciantes são integralmente responsáveis pelas informações divulgadas.
                </p>
              </div>

              {/* 14. PROPRIEDADE INTELECTUAL */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. PROPRIEDADE INTELECTUAL</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A marca Brechó Vizinho, seus logotipos, identidade visual, textos, imagens, funcionalidades e demais elementos da 
                  plataforma são protegidos pela legislação aplicável.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  É proibida a reprodução ou utilização sem autorização prévia.
                </p>
              </div>

              {/* 15. EXPANSÃO GEOGRÁFICA - NUEVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">15. EXPANSÃO GEOGRÁFICA</h2>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho reserva-se o direito de limitar ou expandir a utilização da plataforma para determinadas 
                  comunidades, condomínios, bairros ou regiões geográficas.
                </p>
              </div>

              {/* 16. PRIVACIDADE E DADOS PESSOAIS - NUEVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">16. PRIVACIDADE E DADOS PESSOAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O tratamento de dados pessoais é realizado conforme descrito na Política de Privacidade da plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ao utilizar o Brechó Vizinho, o usuário declara estar ciente da Política de Privacidade e das regras de tratamento 
                  de dados pessoais.
                </p>
              </div>

              {/* 17. DOCUMENTOS COMPLEMENTARES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">17. DOCUMENTOS COMPLEMENTARES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização da plataforma também está sujeita às demais políticas e documentos complementares disponibilizados pelo 
                  Brechó Vizinho, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Política de Privacidade;</li>
                  <li>Política da Comunidade;</li>
                  <li>Política de Publicidade;</li>
                  <li>Política de Cookies;</li>
                  <li>Política de Reembolso;</li>
                  <li>Política de Validação de Moradores e Propriedades;</li>
                  <li>Política de Chat e Mensagens.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Esses documentos complementam os presentes Termos de Uso e devem ser interpretados em conjunto.
                </p>
              </div>

              {/* 18. ALTERAÇÕES DOS TERMOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">18. ALTERAÇÕES DOS TERMOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá atualizar estes Termos de Uso a qualquer momento.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Quando aplicável, os usuários serão informados sobre alterações relevantes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A continuidade do uso da plataforma após a publicação das alterações será considerada como concordância com a nova 
                  versão.
                </p>
              </div>

              {/* 19. ENCERRAMENTO DE CONTAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">19. ENCERRAMENTO DE CONTAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá solicitar o encerramento de sua conta a qualquer momento.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá suspender ou encerrar contas que violem estes Termos, a legislação vigente ou as diretrizes 
                  da comunidade.
                </p>
              </div>

              {/* 20. LEGISLAÇÃO APLICÁVEL */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">20. LEGISLAÇÃO APLICÁVEL</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Estes Termos serão regidos pelas leis da República Federativa do Brasil.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Eventuais controvérsias serão submetidas ao foro legalmente competente, observadas as disposições da legislação 
                  aplicável.
                </p>
              </div>

              {/* 21. CONTATO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">21. CONTATO</h2>
                <p className="text-gray-600 leading-relaxed">
                  Em caso de dúvidas, solicitações ou comunicações relacionadas a estes Termos de Uso, o usuário poderá entrar em 
                  contato através dos canais oficiais disponibilizados pela plataforma.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}